import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Lightbulb, BarChart3, Zap, Award, Bot, BrainCircuit, Workflow, Sparkles, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getServices, getBlogPosts } from '@/lib/api';
import { FAQSection } from '@/components/FAQSection';
import PAGE_FAQS from '@/data/pageFaqs';
import { PageMeta } from '@/components/PageMeta';
import { ICON_MAP, SERVICE_COLORS, SERVICES_ORDER, SERVICES_BY_ID } from '@/data/servicesData';

const STATS = [
  { value: '130+', label: 'Projets réalisés', icon: Award },
  { value: '98%', label: 'Clients satisfaits', icon: Zap },
  { value: '10', label: "Années d'expérience", icon: BarChart3 },
  { value: '5+', label: 'Experts dédiés', icon: Users },
];

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

export default function Home() {
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    getServices().then(setServices).catch(() => {});
    getBlogPosts().then(p => setPosts(p.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <PageMeta title="Lazuli Marketing | Agence Marketing & Branding" description="Lazuli Marketing accompagne les entreprises B2B dans leur transformation digitale avec des stratégies marketing sur-mesure et orientées résultats." path="/" />
      {/* Hero - Blue bg */}
      <section data-testid="hero-section" className="bg-[#03228D] pt-28 pb-0 md:pt-36 md:pb-0 relative overflow-hidden" style={{ minHeight: '75vh' }}>
        {/* Animated decorative circles */}
        <div className="absolute w-[400px] h-[400px] rounded-full bg-white/[0.06] -top-32 -right-32 anim-float-slow" />
        <div className="absolute w-[180px] h-[180px] rounded-full bg-white/[0.10] top-28 right-[20%] anim-float-med" />
        <div className="absolute w-[120px] h-[120px] rounded-full border-[3px] border-white/[0.12] bottom-24 left-[5%] anim-float-slow" style={{ animationDelay: '1s' }} />
        <div className="absolute w-[45px] h-[45px] rounded-full bg-white/[0.15] bottom-40 left-[30%] anim-float-fast" />
        <div className="absolute w-[60px] h-[60px] rounded-full bg-white/[0.05] anim-float-fast" style={{ top: '45%', left: '46%' }} />
        <div className="absolute w-[100px] h-[100px] rounded-full border-2 border-white/[0.08] anim-drift" style={{ top: '35%', left: '38%' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 flex items-center h-full pb-16">
          <div className="max-w-xl lg:max-w-[50%]">
            <p className="font-cabin text-sm uppercase tracking-[0.2em] text-[#FF8C4B] mb-6 font-semibold">
              Performance Marketing, Expérience Client, Branding
            </p>
            <h1 className="font-cabin text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
              Propulsez votre marque vers de{' '}
              <span className="text-[#FF8C4B]">nouveaux sommets</span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-xl font-dm mb-10">
              Lazuli Marketing accompagne la croissance de ses clients en déployant des stratégies orientées résultats couplée à une offre complète de services opérationnels.
            </p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="flex flex-wrap gap-4">
              <Link to="/services">
                <Button data-testid="hero-services-btn" className="bg-[#FF8C4B] hover:bg-[#e07a3e] text-white shadow-[0_4px_16px_rgba(255,140,75,0.3)] rounded-[5px] px-8 py-3 text-base btn-shine">
                  Nos prestations <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-contact-btn" variant="outline" className="border-2 border-white/30 text-white hover:bg-white hover:text-[#03228D] rounded-[5px] px-8 py-3 text-base">
                  Contactez-nous
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Hero image - absolute, touching bottom like ServiceHero */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.8 }} className="hidden lg:block absolute bottom-0 right-[max(24px,calc((100%-1280px)/2+24px))] z-10">
          <div className="relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[45%] w-[400px] h-[400px] rounded-full bg-white/[0.08] anim-pulse" />
            <div className="absolute -top-6 -right-10 w-[110px] h-[110px] rounded-full bg-white/[0.12] anim-float-med" />
            <div className="absolute top-[20%] -left-10 w-[70px] h-[70px] rounded-full border-[3px] border-white/[0.15] anim-float-slow" style={{ animationDelay: '1.5s' }} />
            <div className="absolute top-[12%] right-[8%] w-[20px] h-[20px] rounded-full bg-white/[0.25] anim-drift" />
            <img src="/hero_person.webp" alt="Lazuli Marketing — Agence marketing B2B" className="relative z-10 max-h-[calc(75vh-160px)] w-auto object-contain object-bottom drop-shadow-[0_10px_30px_rgba(0,0,0,0.25)]" />
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
          <div className="flex flex-col items-center gap-1.5 text-white/60">
            <span className="text-[10px] font-cabin uppercase tracking-widest">Scroll</span>
            <div className="w-[22px] h-[34px] rounded-full border-2 border-white/30 flex justify-center pt-1.5">
              <div className="w-[3px] h-[8px] rounded-full bg-white/60 animate-scroll-dot" />
            </div>
          </div>
        </div>
      </section>

      {/* Services - Grey bg with white cards */}
      <section data-testid="services-section" className="section-grey py-20 md:py-32 relative overflow-hidden">
        <div className="absolute w-[280px] h-[280px] rounded-full bg-[#03228D] opacity-[0.05] -bottom-16 -left-16 anim-float-slow" />
        <div className="absolute w-[140px] h-[140px] rounded-full border-[3px] border-[#03228D] opacity-[0.12] top-16 right-12 anim-float-med" style={{ animationDelay: '1s' }} />
        <div className="absolute w-[50px] h-[50px] rounded-full bg-[#03228D] opacity-[0.15] top-32 right-[32%] anim-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute w-[20px] h-[20px] rounded-full bg-[#03228D] opacity-[0.25] bottom-[20%] right-[15%] anim-float-fast" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-16">
            <motion.p variants={fadeUp} className="font-cabin text-sm uppercase tracking-[0.2em] text-[#03228D] mb-4 font-semibold">Ce que nous faisons</motion.p>
            <motion.h2 variants={fadeUp} className="font-cabin text-3xl md:text-5xl font-semibold text-[#141C39] tracking-tight">Nos expertises</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES_ORDER.map((id) => {
              const svc = services.find(s => s.id === id) || SERVICES_BY_ID[id];
              if (!svc) return null;
              const Icon = ICON_MAP[svc.icon] || Lightbulb;
              const color = SERVICE_COLORS[svc.id] || '#03228D';
              return (
                <div key={svc.id} className="stagger-item">
                  <Link to={`/services/${svc.slug}`} data-testid={`service-card-${svc.slug}`} className="block block-card rounded-[5px] p-6 h-full group">
                    <div className="svc-icon-wrap w-12 h-12 rounded-[5px] flex items-center justify-center mb-5" style={{ backgroundColor: `${color}18`, '--svc-color': color }}>
                      <Icon className="svc-icon-inner w-6 h-6" style={{ color }} />
                    </div>
                    <h3 className="font-cabin text-lg font-semibold text-[#141C39] mb-3">{svc.title}</h3>
                    <p className="text-sm text-[#141C39]/60 font-dm leading-relaxed mb-4">{svc.short_desc}</p>
                    <span className="inline-flex items-center text-sm font-semibold font-cabin uppercase tracking-wide group-hover:translate-x-1 transition-transform" style={{ color }}>
                      Découvrir <ArrowRight className="ml-1 w-3 h-3" />
                    </span>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blue block feature section - Photo left, text right */}
      <section className="bg-[#03228D] pb-0 pt-20 md:pt-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-white/5" style={{ transform: 'translate(30%, -30%)' }} />
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-end relative z-10">
          <div className="hidden lg:flex justify-center items-end">
            <img src="/approche_person.webp" alt="Notre approche marketing personnalisée" loading="lazy" className="w-[520px] object-contain object-bottom drop-shadow-[0_10px_30px_rgba(0,0,0,0.25)]" />
          </div>
          <div className="pb-20 md:pb-28">
            <p className="font-cabin text-sm uppercase tracking-[0.2em] text-[#FF8C4B] mb-4 font-semibold">Notre approche</p>
            <h2 className="font-cabin text-3xl md:text-4xl font-semibold text-white tracking-tight mb-6">
              Des stratégies sur-mesure pour chaque client
            </h2>
            <p className="text-white/70 font-dm leading-relaxed mb-8">
              Chez Lazuli Marketing, chaque projet est unique. Nous combinons expertise stratégique, créativité et données pour concevoir des solutions marketing qui génèrent des résultats concrets et mesurables.
            </p>
            <Link to="/agence">
              <Button data-testid="home-about-btn" className="bg-white text-[#03228D] hover:bg-[#FFF3DB] rounded-[5px] px-8 py-3 shadow-lg font-cabin uppercase tracking-wide text-sm">
                Découvrir l'agence <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats - White bg, large blue numbers */}
      <section data-testid="stats-section" className="bg-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute w-[180px] h-[180px] rounded-full border-[3px] border-[#03228D] opacity-[0.12] top-8 right-16 anim-float-slow" style={{ animationDelay: '0.5s' }} />
        <div className="absolute w-[100px] h-[100px] rounded-full bg-[#03228D] opacity-[0.06] bottom-8 left-16 anim-float-med" />
        <div className="absolute w-[30px] h-[30px] rounded-full bg-[#03228D] opacity-[0.2] top-[45%] left-[8%] anim-float-fast" style={{ animationDelay: '1.5s' }} />
        <div className="absolute w-[65px] h-[65px] rounded-full bg-[#03228D] opacity-[0.08] bottom-16 right-[30%] anim-drift" />
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-cabin text-sm uppercase tracking-[0.2em] text-[#03228D] mb-4 font-semibold">En chiffres</p>
            <h2 className="font-cabin text-3xl md:text-4xl font-semibold text-[#141C39] tracking-tight">Les résultats parlent d'eux-mêmes</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center stagger-item">
                <p className="font-cabin text-5xl md:text-6xl font-bold text-[#03228D] mb-3">{stat.value}</p>
                <p className="text-sm text-[#141C39]/50 font-dm font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOCUS IA & DATA - Blue block */}
      <section data-testid="focus-ia-section" className="bg-[#03228D] py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-white/5" style={{ transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-[250px] h-[250px] rounded-full bg-white/5" style={{ transform: 'translate(-30%, 30%)' }} />
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-[#7C3AED]/20 text-[#C4B5FD] rounded-full px-4 py-1.5 mb-6">
                <BrainCircuit className="w-4 h-4" />
                <span className="text-xs font-cabin uppercase tracking-wider font-semibold">Focus IA & Data</span>
              </motion.div>
              <motion.h2 variants={fadeUp} className="font-cabin text-3xl md:text-4xl font-semibold text-white tracking-tight mb-6">
                L'intelligence artificielle au c&oelig;ur de notre approche
              </motion.h2>
              <motion.p variants={fadeUp} className="text-white/70 font-dm leading-relaxed mb-6">
                Chez Lazuli Marketing, nous intégrons l'IA et la data au c&oelig;ur de chaque stratégie. De l'automatisation des campagnes à l'analyse prédictive, nous exploitons les dernières technologies pour maximiser vos performances marketing.
              </motion.p>
              <motion.div variants={fadeUp} className="space-y-3 mb-8">
                {[
                  'Marketing automation & workflows intelligents',
                  'Analyse prédictive & lead scoring IA',
                  'Chatbots & assistants conversationnels',
                  'Data analytics & reporting en temps réel'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#7C3AED]/25 flex items-center justify-center shrink-0">
                      <Zap className="w-3 h-3 text-[#C4B5FD]" />
                    </div>
                    <span className="text-sm text-white/80 font-dm">{item}</span>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeUp}>
                <Link to="/services/ia-automatisation">
                  <Button data-testid="focus-ia-btn" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-[0_4px_16px_rgba(124,58,237,0.4)] rounded-[5px] px-8 py-3 text-base font-cabin uppercase tracking-wide">
                    Découvrir nos solutions IA <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Bot, label: 'Chatbots IA', color: '#7C3AED', value: '24/7' },
                  { icon: BarChart3, label: 'Analytics', color: '#0891B2', value: '+45%' },
                  { icon: Workflow, label: 'Automation', color: '#059669', value: '3x' },
                  { icon: Sparkles, label: 'Lead scoring', color: '#DC2626', value: '98%' },
                ].map((card) => {
                  const Icon = card.icon;
                  return (
                    <div key={card.label} data-testid={`focus-card-${card.label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white/10 backdrop-blur-sm rounded-[5px] p-6 text-center group hover:bg-white/15 transition-colors">
                      <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${card.color}15` }}>
                        <Icon className="w-6 h-6" style={{ color: card.color }} />
                      </div>
                      <p className="font-cabin text-2xl font-bold mb-1 text-white">{card.value}</p>
                      <p className="text-xs text-white/50 font-dm uppercase tracking-wider">{card.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview - Grey bg */}
      {posts.length > 0 && (
        <section data-testid="blog-preview-section" className="section-grey py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="font-cabin text-sm uppercase tracking-[0.2em] text-[#03228D] mb-4 font-semibold">Actualités</p>
                <h2 className="font-cabin text-3xl md:text-4xl font-semibold text-[#141C39] tracking-tight">Dernières publications</h2>
              </div>
              <Link to="/ressources" className="hidden md:flex items-center text-sm text-[#03228D] hover:text-[#FF8C4B] font-cabin uppercase tracking-wide transition-colors">
                Tout voir <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="block-card rounded-[5px] overflow-hidden group">
                  {post.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img src={post.image_url} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-xs text-[#03228D] font-cabin uppercase tracking-wider font-semibold">{post.category === 'actualites' ? 'Actualité' : 'Blog Expert'}</span>
                    <h3 className="font-cabin text-lg font-semibold text-[#141C39] mt-2 mb-3 group-hover:text-[#03228D] transition-colors">{post.title}</h3>
                    <p className="text-sm text-[#141C39]/50 font-dm line-clamp-2">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <FAQSection faqs={PAGE_FAQS.home} />

      {/* CTA - Blue block */}
      <section data-testid="cta-section" className="bg-[#03228D] py-20 md:py-28 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-white/5" style={{ transform: 'translate(-30%, 30%)' }} />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-cabin text-3xl md:text-5xl font-semibold text-white tracking-tight mb-6">
              Prêt à transformer votre marketing ?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/70 font-dm mb-10">
              Discutons ensemble de votre projet et définissons la meilleure stratégie pour atteindre vos objectifs.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/contact?rdv=true">
                <Button data-testid="cta-contact-btn" className="bg-[#FF8C4B] hover:bg-[#e07a3e] text-white rounded-[5px] px-8 py-3 text-base shadow-lg font-cabin uppercase tracking-wide">
                  Prendre rendez-vous
                </Button>
              </Link>
              <Link to="/services">
                <Button data-testid="cta-services-btn" variant="outline" className="border-2 border-white/30 text-white hover:bg-white hover:text-[#03228D] rounded-[5px] px-8 py-3 text-base">
                  Voir nos services
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
